import { useId, useRef, useState, type FormEvent } from "react";
import { Check, CircleAlert, X } from "lucide-react";

import {
  Modal,
  PrimaryCommandButton,
  SegmentedControl,
  type SegmentedControlOption
} from "@/ui";
import { childProfileSchema } from "@/domain/schemas";

import {
  PROFILE_AVATAR_OPTIONS,
  type PresetAvatarId
} from "./avatar-options";
import "./profiles.css";

const DEFAULT_AVATAR_ID: PresetAvatarId = "captain-blue";
const nicknameSchema = childProfileSchema.shape.nickname;

const avatarControlOptions = PROFILE_AVATAR_OPTIONS.map((avatar) => ({
  value: avatar.id,
  label: avatar.label,
  icon: avatar.icon
})) satisfies ReadonlyArray<SegmentedControlOption<PresetAvatarId>>;

export interface CreateProfileInput {
  readonly nickname: string;
  readonly avatarId: PresetAvatarId;
}

export interface ProfileCreateDialogProps {
  readonly busy: boolean;
  readonly errorMessage: string | null;
  readonly onCancel: () => void;
  readonly onCreate: (input: CreateProfileInput) => void;
}

function normalizeNickname(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/gu, " ");
}

export function ProfileCreateDialog({
  busy,
  errorMessage,
  onCancel,
  onCreate
}: ProfileCreateDialogProps) {
  const formId = useId();
  const nicknameHintId = useId();
  const nicknameErrorId = useId();
  const nicknameInputRef = useRef<HTMLInputElement>(null);
  const [nickname, setNickname] = useState("");
  const [avatarId, setAvatarId] =
    useState<PresetAvatarId>(DEFAULT_AVATAR_ID);
  const [nicknameError, setNicknameError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedNickname = normalizeNickname(nickname);

    if (!nicknameSchema.safeParse(normalizedNickname).success) {
      setNicknameError(
        normalizedNickname.length === 0
          ? "先给小船员取一个小名吧。"
          : "这个小名有点长，换一个短一点的吧。"
      );
      nicknameInputRef.current?.focus();
      return;
    }

    setNicknameError(null);
    onCreate({ nickname: normalizedNickname, avatarId });
  }

  const describedBy = [
    nicknameHintId,
    nicknameError ? nicknameErrorId : null
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Modal
      actions={
        <>
          <button
            className="profile-create-dialog__cancel"
            disabled={busy}
            onClick={onCancel}
            type="button"
          >
            <X aria-hidden="true" />
            先等等
          </button>
          <PrimaryCommandButton
            disabled={busy}
            form={formId}
            icon={Check}
            type="submit"
          >
            {busy ? "正在保存" : "保存并出发"}
          </PrimaryCommandButton>
        </>
      }
      className="profile-create-dialog"
      closeLabel="关闭新建小船员"
      initialFocusRef={nicknameInputRef}
      onClose={onCancel}
      open
      title="新建小船员"
    >
      <form
        className="profile-create-dialog__form"
        id={formId}
        onSubmit={handleSubmit}
      >
        <div className="profile-create-dialog__field">
          <label htmlFor={formId + "-nickname"}>小名</label>
          <input
            aria-describedby={describedBy}
            aria-invalid={nicknameError !== null}
            autoComplete="off"
            disabled={busy}
            id={formId + "-nickname"}
            maxLength={nicknameSchema.maxLength ?? undefined}
            onChange={(event) => {
              setNickname(event.target.value);
              setNicknameError(null);
            }}
            placeholder="比如：朵朵"
            ref={nicknameInputRef}
            spellCheck={false}
            type="text"
            value={nickname}
          />
          <p className="profile-create-dialog__hint" id={nicknameHintId}>
            用熟悉的小名就好，不用填写真实姓名。
          </p>
          {nicknameError ? (
            <p
              className="profile-create-dialog__field-error"
              id={nicknameErrorId}
              role="alert"
            >
              <CircleAlert aria-hidden="true" />
              {nicknameError}
            </p>
          ) : null}
        </div>

        <SegmentedControl
          className="profile-create-dialog__avatars"
          label="选择启航徽章"
          onChange={setAvatarId}
          options={avatarControlOptions}
          value={avatarId}
        />

        {errorMessage ? (
          <p className="profile-create-dialog__save-error" role="alert">
            <CircleAlert aria-hidden="true" />
            {errorMessage}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
